import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, BarChart3, Check, ChevronRight, Download, Loader2, RotateCcw, X,
} from "lucide-react";
import { API, formatApiError } from "../auth/api";

/**
 * Overblik (2026-10-23, runde 10) - erstatter den gamle Rapportering.
 * Ét sted at se, hvordan firmaet går: omsætning pr. kunde, lokation,
 * leveringssted, materiale, opgave og chauffør (klik for at se nærmere),
 * timer, løn og dækningsbidrag, for i dag, ugen, måneden, 3/6/12 måneder
 * eller en egen periode - sammenlignet med perioden før.
 * Tallene er de samme som i Økonomi › Rapport (omsætning) og Løn (timer og
 * lønomkostning). Backend: GET /api/admin/overview.
 */

const card = "bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const chipBase = "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors";
const chipOn = "bg-emerald-600 border-emerald-600 text-white";
const chipOff = "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700";
const input = "px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50";

const PRESETS = [["today", "I dag"], ["week", "Denne uge"], ["month", "Denne måned"], ["3m", "3 måneder"],
  ["6m", "6 måneder"], ["1y", "1 år"], ["custom", "Egen periode"]];
const DIMS = [["customer", "Kunder"], ["location", "Lokationer"], ["delivery", "Leveringssteder"],
  ["material", "Materialer"], ["task", "Opgaver"], ["driver", "Chauffører"]];
const DIM_ONE = { customer: "Kunde", location: "Lokation", delivery: "Leveringssted", material: "Materiale", task: "Opgave", driver: "Chauffør" };
const BUCKET = { day: "dag", week: "uge", month: "måned" };
const STORE_KEY = "cf_overview_preset";

const nf = (n, d = 0) => Number(n || 0).toLocaleString("da-DK", { minimumFractionDigits: d, maximumFractionDigits: d });
const kr = (n) => (n == null ? "–" : `${nf(n)} kr`);
const hrs = (m) => (m == null ? "–" : nf((m || 0) / 60, 1));
const pctTxt = (v) => (v == null ? "–" : `${nf(v, 1)} %`);
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const errText = (e, fb) => (e?.response?.data?.detail != null ? formatApiError(e) : fb);
// Ændring i % mod perioden før (null når der intet var at sammenligne med)
const changePct = (cur, prev) => (prev ? ((cur - prev) / Math.abs(prev)) * 100 : null);
const change = changePct;

function readPreset() {
  try { return localStorage.getItem(STORE_KEY) || "month"; } catch (_) { return "month"; }
}

export default function OverviewPanel() {
  const [preset, setPreset] = useState(readPreset);
  const [custom, setCustom] = useState(() => {
    const d = new Date();
    return { from: localDate(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: localDate(d) };
  });
  // 2026-10-25: Flere værdier pr. dimension ({driver: [a, b], customer: [c]}).
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dim, setDim] = useState("customer");
  const [metric, setMetric] = useState("revenue");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // Gentagne parametre (?driver=a&driver=b) - serveren lægger dem sammen.
      const params = new URLSearchParams({ preset });
      if (preset === "custom") { params.set("date_from", custom.from); params.set("date_to", custom.to); }
      Object.entries(filters).forEach(([k, vs]) => vs.forEach((v) => params.append(k, v)));
      const r = await axios.get(`${API}/admin/overview`, { params });
      setData(r.data);
    } catch (e) {
      if (!silent) toast.error(errText(e, "Kunne ikke hente overblikket"));
    } finally { if (!silent) setLoading(false); }
  }, [preset, filters, custom]);

  useEffect(() => { load(); }, [load]);
  // I dag er live: hent igen hvert minut, mens fanen er synlig.
  useEffect(() => {
    if (preset !== "today") return undefined;
    const iv = setInterval(() => { if (!document.hidden) load(true); }, 60000);
    return () => clearInterval(iv);
  }, [preset, load]);

  const choosePreset = (p) => {
    setPreset(p);
    try { localStorage.setItem(STORE_KEY, p); } catch (_) { /* privat vindue */ }
  };
  // Klik på en række: vælg den (eller fjern den igen) - flere i samme dimension lægges sammen.
  const toggleFilter = (k, v) => setFilters((f) => {
    const cur = f[k] || [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    const n = { ...f, [k]: next };
    if (!next.length) delete n[k];
    return n;
  });

  const dims = useMemo(() => DIMS.filter(([k]) => data?.dims?.[k]?.length), [data]);
  useEffect(() => {
    if (data && !data.dims?.[dim]?.length && dims.length) setDim(dims[0][0]);
  }, [data, dim, dims]);
  const access = data?.access || {};
  useEffect(() => {
    if (data && metric === "revenue" && !access.money) setMetric("tours");
  }, [data, metric, access.money]);

  return (
    <div className="space-y-4" data-testid="overview-panel">
      <div className={`${card} p-4 space-y-3`}>
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-[220px]">
            <h2 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-600" />Overblik</h2>
            {data && (
              <div className="text-xs text-slate-500" data-testid="overview-range">
                {data.range.label} · sammenlignet med {data.range.prev_label}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            <button className={btnGhost} onClick={() => load()} title="Hent igen"><RotateCcw className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map(([k, l]) => (
            <button key={k} onClick={() => choosePreset(k)} className={`${chipBase} ${preset === k ? chipOn : chipOff}`} data-testid={`overview-preset-${k}`}>{l}</button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-slate-500">Fra<br /><input type="date" className={input} value={custom.from} max={custom.to} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} /></label>
            <label className="text-xs text-slate-500">Til<br /><input type="date" className={input} value={custom.to} min={custom.from} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} /></label>
          </div>
        )}
        {data?.filters?.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="overview-filters">
            <span className="text-xs text-slate-500">Viser kun:</span>
            {data.filters.map((f) => (
              <span key={`${f.key}-${f.value}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 text-xs font-semibold">
                {DIM_ONE[f.key]}: {f.label}
                <button onClick={() => toggleFilter(f.key, f.value)} aria-label={`Fjern ${f.label}`}><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
            <button className="text-xs font-semibold text-slate-500 hover:underline" onClick={() => setFilters({})}>Vis alt</button>
          </div>
        )}
      </div>

      {!data && loading && <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
      {data && (
        <>
          {data.live && <LiveToday live={data.live} />}
          <KpiGrid k={data.kpis} p={data.prev_kpis} access={access} partial={data.range.preset === "today"} />
          {access.money && data.kpis.unpriced > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-2.5 text-sm text-amber-900 dark:text-amber-100 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {data.kpis.unpriced} {data.kpis.unpriced === 1 ? "tur mangler" : "ture mangler"} en pris, så omsætningen er for lav. Ret det i Økonomi › Rapport.
            </div>
          )}
          {data.trend.length > 0 && <Trend trend={data.trend} bucket={data.range.bucket} metric={metric} setMetric={setMetric} access={access} />}
          {dims.length > 0 && (
            <Breakdown data={data} dims={dims} dim={dim} setDim={setDim} onPick={toggleFilter} access={access} />
          )}
          <div className="text-xs text-slate-500 space-y-1 px-1">
            <p>Ture = kørte og forgæves ture efter køredag, som i Økonomi › Rapport.{access.money ? " Omsætning er ekskl. moms." : ""}
              {access.people ? " Timer og overtid er fra Løn (efter pause og afrunding)." : ""}
              {access.rates ? " Lønomkostning er et skøn ud fra satserne i Løn › Regler (fast løn er ikke med); dækningsbidrag er før bil og andre omkostninger." : ""}</p>
            {data.people_note && <p>{data.people_note}</p>}
          </div>
        </>
      )}
    </div>
  );
}

function LiveToday({ live }) {
  const tiles = [["Planlagte ture", live.planned], ["Kørt", live.done], ["På vej", live.on_way], ["Uden chauffør", live.unassigned],
    ["Haster, ikke kørt", live.urgent_open], ["Ikke kørt (14 dage)", live.overdue]];
  return (
    <div className={`${card} p-4 space-y-3`} data-testid="overview-live">
      <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white">
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Lige nu – i dag
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {tiles.map(([l, v]) => (
          <div key={l} className="rounded-xl bg-slate-50 dark:bg-slate-900/40 px-3 py-2">
            <div className="text-xl font-extrabold tabular-nums text-slate-900 dark:text-white">{v}</div>
            <div className="text-[11px] text-slate-500">{l}</div>
          </div>
        ))}
      </div>
      {live.planned > 0 && (
        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden flex" aria-hidden>
          <div className="bg-emerald-600" style={{ width: `${(live.done / live.planned) * 100}%` }} />
          <div className="bg-amber-500 ml-0.5" style={{ width: `${(live.on_way / live.planned) * 100}%` }} />
        </div>
      )}
      <div className="text-sm text-slate-600 dark:text-slate-300 flex flex-wrap gap-x-6 gap-y-1">
        <span data-testid="ov-stamped-in"><b>Stemplet ind:</b> {live.stamped_in.length ? live.stamped_in.map(n => ((live.on_pause || []).includes(n) ? `${n} (pause)` : n)).join(", ") : "ingen"}</span>
        <span><b>Fravær i dag:</b> {live.absent.length ? live.absent.map((a) => `${a.name} (${a.label}${a.part === "rest" ? ", resten af dagen" : ""})`).join(", ") : "ingen"}</span>
      </div>
    </div>
  );
}

function KpiGrid({ k, p, access, partial }) {
  // I dag er ikke slut endnu - en procent mod hele dagen sidste uge ville snyde.
  const change = partial ? () => null : changePct;
  const tiles = [];
  const was = partial ? "samme dag sidste uge" : "før";
  if (access.money) {
    tiles.push({ l: "Omsætning", v: kr(k.revenue), d: change(k.revenue, p.revenue), sub: `${was}: ${kr(p.revenue)}`, testid: "overview-kpi-revenue" });
  }
  tiles.push({ l: "Ture", v: nf(k.tours), d: change(k.tours, p.tours), sub: `${k.failed ? `${k.failed} forgæves · ` : ""}${was}: ${nf(p.tours)}`, testid: "overview-kpi-tours" });
  if (access.money) tiles.push({ l: "Gns. pris pr. tur", v: kr(k.avg_price), d: change(k.avg_price, p.avg_price), sub: `${was}: ${kr(p.avg_price)}` });
  if (access.people && access.rates && k.db != null) {
    tiles.push({ l: "Dækningsbidrag", v: kr(k.db), d: change(k.db, p.db), sub: `${pctTxt(k.db_pct)} af omsætningen · før bil`, tone: k.db < 0 ? "red" : "emerald" });
  }
  if (access.people) {
    tiles.push({ l: "Arbejdstimer", v: hrs(k.work), d: change(k.work, p.work), sub: `${k.drivers} chauffører med ture · ${was}: ${hrs(p.work)} t` });
    if (access.money) tiles.push({ l: "Omsætning pr. arbejdstime", v: kr(k.revenue_per_hour), d: change(k.revenue_per_hour, p.revenue_per_hour), sub: `${was}: ${kr(p.revenue_per_hour)}` });
    if (access.rates) tiles.push({ l: "Lønomkostning", v: kr(k.cost), d: change(k.cost, p.cost), sub: `${was}: ${kr(p.cost)}`, goodDown: true });
    tiles.push({ l: "Overtid og sygefravær", v: `${pctTxt(k.ot_pct)} · ${pctTxt(k.sick_pct)}`, sub: `${k.sick_days} sygedage · ${k.vacation_days} feriedage`, title: "Overtid i % af timerne · sygetimer i % af de planlagte timer" });
    // 2026-10-25: Kg og tid pr. tur som i den gamle Rapportering - også med kroner og timer.
    if (k.kg) tiles.push({ l: "Kg", v: nf(k.kg), d: change(k.kg, p.kg), sub: `${was}: ${nf(p.kg)}` });
    if (k.avg_min) tiles.push({ l: "Gns. tid pr. tur", v: `${k.avg_min} min`, sub: `${nf(k.days ? k.tours / k.days : 0, 1)} ture pr. køredag` });
  } else {
    if (k.kg) tiles.push({ l: "Kg", v: nf(k.kg), d: change(k.kg, p.kg), sub: `${was}: ${nf(p.kg)}` });
    tiles.push({ l: "Chauffører med ture", v: nf(k.drivers), sub: `${k.days} køredage` });
    if (k.avg_min) tiles.push({ l: "Gns. tid pr. tur", v: `${k.avg_min} min`, sub: "fra på vej til afsluttet" });
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {tiles.map((t) => <Stat key={t.l} {...t} />)}
    </div>
  );
}

function Stat({ l, v, d, sub, tone, goodDown, title, testid }) {
  const show = d != null && Number.isFinite(d) && Math.abs(d) >= 0.5;
  const good = show && (goodDown ? d < 0 : d > 0);
  const Arrow = d > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <div className={`${card} px-4 py-3`} title={title} data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{l}</div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <div className={`text-2xl font-extrabold tabular-nums ${tone === "red" ? "text-red-700" : tone === "emerald" ? "text-emerald-700" : "text-slate-900 dark:text-white"}`}>{v}</div>
        {show && (
          <span className={`inline-flex items-center text-xs font-bold ${good ? "text-emerald-700" : "text-red-700"}`}>
            <Arrow className="w-3.5 h-3.5" aria-hidden />{nf(Math.abs(d), 0)} %
          </span>
        )}
      </div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

function Trend({ trend, bucket, metric, setMetric, access }) {
  const [hover, setHover] = useState(null);
  const metrics = [access.money && ["revenue", "Omsætning"], ["tours", "Ture"], access.people && ["work", "Arbejdstimer"]].filter(Boolean);
  const val = (b) => (metric === "work" ? (b.work || 0) / 60 : b[metric] || 0);
  const fmt = (b) => (metric === "revenue" ? kr(b.revenue) : metric === "work" ? `${hrs(b.work)} t` : `${nf(b.tours)} ture`);
  const max = Math.max(1, ...trend.map(val));
  const H = 140;
  const showLabels = trend.length <= 14;
  return (
    <div className={`${card} p-4`} data-testid="overview-trend">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-bold text-slate-900 dark:text-white">{metrics.find(([k]) => k === metric)?.[1]} pr. {BUCKET[bucket]}</div>
        <div className="flex gap-1.5">
          {metrics.map(([k, l]) => (
            <button key={k} onClick={() => setMetric(k)} className={`${chipBase} ${metric === k ? chipOn : chipOff}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-3 flex" onMouseLeave={() => setHover(null)}>
        {trend.map((b, i) => (
          <div key={b.key} className="flex-1 min-w-0 relative" onMouseEnter={() => setHover(i)}>
            <div className="flex flex-col items-center justify-end px-[2px] sm:px-1 border-b border-slate-200 dark:border-slate-700" style={{ height: H + (showLabels ? 18 : 4) }}>
              {showLabels && <div className="text-[10px] text-slate-600 dark:text-slate-300 tabular-nums mb-1 whitespace-nowrap">{val(b) ? (metric === "revenue" ? nf(b.revenue / 1000, val(b) < 10000 ? 1 : 0) + "k" : metric === "work" ? nf(val(b)) : nf(b.tours)) : ""}</div>}
              <div className={`w-full max-w-[40px] rounded-t ${hover === i ? "bg-emerald-700" : "bg-emerald-600"}`} style={{ height: Math.max(val(b) ? 2 : 0, (val(b) / max) * H) }} />
            </div>
            <div className="text-[10px] mt-1 text-center text-slate-500 truncate">{trend.length > 20 && i % Math.ceil(trend.length / 10) !== 0 ? "" : b.label}</div>
            {hover === i && (
              <div className={`absolute bottom-full mb-1 z-10 whitespace-nowrap rounded-lg bg-slate-900 text-white text-[11px] px-2 py-1 shadow ${i > trend.length / 2 ? "right-0" : "left-0"}`}>
                {b.long}<br />{fmt(b)}{metric !== "tours" ? ` · ${nf(b.tours)} ture` : ""}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Breakdown({ data, dims, dim, setDim, onPick, access }) {
  const [sort, setSort] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => { setSort(null); setAll(false); }, [dim]);
  const rows = useMemo(() => {
    const list = [...(data.dims[dim] || [])];
    if (sort) {
      const [k, dir] = sort;
      list.sort((a, b) => (k === "label" ? String(a.label).localeCompare(String(b.label), "da") : (Number(a[k] ?? -Infinity) - Number(b[k] ?? -Infinity))) * dir);
    }
    return list;
  }, [data, dim, sort]);
  const money = access.money;
  const people = dim === "driver" && access.people;
  // Andelen regnes af fanens egne rækker (de viser alle i dimensionen under de andre filtre).
  const total = rows.reduce((a, r) => a + (money ? r.revenue || 0 : r.tours || 0), 0);
  const anyKg = rows.some((r) => r.kg > 0);
  const shown = all ? rows : rows.slice(0, 12);
  const cols = [
    ["label", DIM_ONE[dim], false],
    ["tours", "Ture"],
    money && ["revenue", "Omsætning"],
    ["share", "Andel", false],
    money && ["avg_price", "Gns. pr. tur"],
    money && ["growth", "Mod før", false],
    anyKg && ["kg", "Kg"],
    dim === "driver" && ["per_day", "Ture/dag"],
    dim === "driver" && ["avg_min", "Min/tur"],
    people && ["work", "Timer"],
    people && money && ["revenue_per_hour", "Oms. pr. time"],
    people && access.rates && ["cost", "Lønomk."],
    people && access.rates && ["db", "DB"],
    people && ["ot_pct", "Overtid"],
    people && ["sick_days", "Syg / ferie"],
  ].filter(Boolean);
  const th = ([k, l, sortable = true]) => (
    <th key={k} className={`px-3 py-2 whitespace-nowrap ${k === "label" ? "text-left" : "text-right"} ${sortable ? "cursor-pointer select-none hover:text-slate-800 dark:hover:text-white" : ""}`}
      onClick={sortable ? () => setSort((s) => [k, s && s[0] === k ? -s[1] : k === "label" ? 1 : -1]) : undefined}>
      {l}{sort && sort[0] === k ? (sort[1] > 0 ? " ↑" : " ↓") : ""}
    </th>
  );
  const exportCsv = () => {
    const head = cols.map(([, l]) => l);
    const body = rows.map((r) => cols.map(([k]) => {
      if (k === "label") return r.label;
      if (k === "share") return total ? nf(((money ? r.revenue : r.tours) / total) * 100, 1) : "";
      if (k === "growth") return r.prev_revenue ? nf(change(r.revenue, r.prev_revenue), 1) : "";
      if (k === "work") return nf((r.work || 0) / 60, 2);
      if (k === "per_day") return r.days ? nf(r.tours / r.days, 1) : "";
      if (k === "sick_days") return `${r.sick_days || 0} / ${r.vacation_days || 0}`;
      return r[k] == null ? "" : String(r[k]).replace(".", ",");
    }));
    const csv = [head, ...body].map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
    a.download = `overblik_${dim}_${data.range.start}_${data.range.end}.csv`;
    a.click();
  };
  return (
    <div className={`${card} overflow-hidden`} data-testid="overview-breakdown">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <div className="flex flex-wrap gap-1.5">
          {dims.map(([k, l]) => (
            <button key={k} onClick={() => setDim(k)} className={`${chipBase} ${dim === k ? chipOn : chipOff}`} data-testid={`overview-dim-${k}`}>
              {l} <span className="opacity-70">{data.dims[k].length}</span>
            </button>
          ))}
        </div>
        <button className={btnGhost} onClick={exportCsv}><Download className="w-4 h-4" />CSV</button>
      </div>
      <div className="px-4 pt-1 text-[11px] text-slate-500">Klik på en række for at vælge den – alt på siden følger med. Vælg flere (fx to chauffører) for at lægge dem sammen; klik igen for at fjerne.</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm mt-2">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-900">
            <tr>{cols.map(th)}<th className="w-6" /></tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const share = total ? ((money ? r.revenue || 0 : r.tours) / total) * 100 : 0;
              const g = money ? change(r.revenue, r.prev_revenue) : change(r.tours, r.prev_tours);
              return (
                <tr key={r.key || "_"} onClick={r.key ? () => onPick(dim, r.key) : undefined}
                  className={`border-t border-slate-100 dark:border-slate-700 group ${r.selected ? "bg-emerald-50 dark:bg-emerald-900/30" : ""} ${r.key ? "hover:bg-emerald-50/60 dark:hover:bg-emerald-900/20 cursor-pointer" : ""}`}
                  aria-selected={!!r.selected} data-testid="overview-row">
                  {cols.map(([k]) => {
                    if (k === "label") return (
                      <td key={k} className="px-3 py-2 min-w-[160px]">
                        <div className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                          {r.selected && <Check className="w-4 h-4 text-emerald-700 shrink-0" aria-label="valgt" />}{r.label}
                        </div>
                        {r.employment_label && <div className="text-[11px] text-slate-500">{r.employment_label}{r.days_worked ? ` · ${r.days_worked} arbejdsdage` : ""}</div>}
                      </td>);
                    if (k === "share") return (
                      <td key={k} className="px-3 py-2 w-40 min-w-[120px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden"><div className="h-full bg-emerald-600 rounded-full" style={{ width: `${Math.min(100, share)}%` }} /></div>
                          <span className="text-xs tabular-nums text-slate-600 dark:text-slate-300 w-10 text-right">{nf(share, share < 10 ? 1 : 0)} %</span>
                        </div>
                      </td>);
                    if (k === "growth") return (
                      <td key={k} className="px-3 py-2 text-right text-xs font-semibold tabular-nums whitespace-nowrap">
                        {g == null ? (r.revenue ? <span className="text-slate-500">ny</span> : "–")
                          : <span className={g >= 0 ? "text-emerald-700" : "text-red-700"}>{g >= 0 ? "+" : ""}{nf(g, 0)} %</span>}
                      </td>);
                    let txt;
                    if (k === "revenue" || k === "avg_price" || k === "cost" || k === "revenue_per_hour") txt = r[k] == null ? "–" : kr(r[k]);
                    else if (k === "db") txt = r.db == null ? "–" : <span className={r.db < 0 ? "text-red-700" : "text-emerald-700"}>{kr(r.db)}</span>;
                    else if (k === "work") txt = r.work == null ? "–" : `${hrs(r.work)} t`;
                    else if (k === "ot_pct") txt = r.ot_pct == null ? "–" : pctTxt(r.ot_pct);
                    else if (k === "sick_days") txt = r.sick_days == null ? "–" : `${r.sick_days} / ${r.vacation_days || 0}`;
                    else if (k === "per_day") txt = r.days ? nf(r.tours / r.days, 1) : "–";
                    else if (k === "avg_min") txt = r.avg_min ? `${r.avg_min} min` : "–";
                    else txt = nf(r[k]);
                    return <td key={k} className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{txt}{k === "tours" && r.unpriced ? <span className="text-amber-700 text-[11px]" title="uden pris"> ({r.unpriced} u. pris)</span> : null}</td>;
                  })}
                  <td className="pr-3 text-slate-300 group-hover:text-emerald-600">{r.selected ? <X className="w-4 h-4" aria-hidden /> : <ChevronRight className="w-4 h-4" />}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > 12 && (
        <button className="w-full py-2 text-xs font-semibold text-emerald-700 hover:bg-slate-50 dark:hover:bg-slate-700 border-t border-slate-100 dark:border-slate-700" onClick={() => setAll((a) => !a)}>
          {all ? "Vis færre" : `Vis alle ${rows.length}`}
        </button>
      )}
      {people && <div className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-100 dark:border-slate-700">Syg / ferie = antal sygedage / feriedage i perioden. Chauffører med timer men uden ture står nederst.</div>}
    </div>
  );
}
